import api from 'api';
import {useEffect, useMemo, useState} from 'react';
import {useMutation, useQuery} from 'react-query';
import {ListResponse, Prescription, Product} from 'types';

const queryFn = async () => {
  const {data} = await api.get<ListResponse<Product>>(`/products/`, {
    params: {
      limit: 150,
    },
  });

  return data.results;
};

type Props = {
  isOpen: boolean;
  requestId: number;
  type: 'product' | 'service';
  product?: Prescription | Prescription[];
  refetchOrder: () => void;
  onClose: () => void;
};

export const useEditModal = ({
  isOpen,
  requestId,
  type,
  product,
  refetchOrder,
  onClose,
}: Props) => {
  const [itemIds, setItemIds] = useState<number[]>([]);
  const [initialItemIds, setInitialItemIds] = useState<number[]>([]);
  const [error, setError] = useState<string>('');
  const [sharedDefaultValue, setSharedDefaultValue] = useState('');
  const {data, isLoading, refetch} = useQuery({
    queryKey: ['edit-modal-items', requestId, type],
    queryFn,
    enabled: isOpen,
  });

  // Initialize itemIds from product prop when modal opens
  useEffect(() => {
    if (isOpen && product) {
      if (Array.isArray(product)) {
        // Multiple products
        const ids = (product as Prescription[]).map((item) => item.drug);
        setItemIds(ids);
        setInitialItemIds(ids);
      }
      setError('');
    }
  }, [isOpen, product, type]);

  const productList = useMemo(() => {
    return data
      ?.filter((item) => item.offering_type === 'product')
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
  }, [data]);

  const serviceList = useMemo(() => {
    return data
      ?.filter((item) => item.offering_type === 'service')
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
  }, [data]);

  const updateRequestProducts = useMutation({
    mutationFn: async () => {
      // Calculate removed items based on initial vs current selection
      const removed = initialItemIds.filter((id) => !itemIds.includes(id));
      const added_drugs = itemIds.filter((id) => !initialItemIds.includes(id));
      const {data} = await api.post(`/requests/${requestId}/items`, {
        request_id: requestId,
        added_drugs,
        removed_drugs: removed,
      });
      return data;
    },
    onSuccess: () => {
      refetch();
      refetchOrder();
      onClose();
    },
  });

  return {
    productList,
    serviceList,
    isLoading,
    refetchOptions: refetch,
    updateRequestProducts,
    setItemIds,
    itemIds,
    error,
    setError,
    sharedDefaultValue,
    setSharedDefaultValue,
  };
};
